<template>
  <div class="p-float-label dropdown-wrapper view-button-input">
    <CustomDropdown :id="id"
                    ref="dd"
                    data-key="id"
                    :disabled="isDisabled"
                    class="dropdown__select"
                    :class="selectClasses"
                    v-model="localValue"
                    :useMinSearchChars="true"
                    :options="cars"
                    :filterPlaceholder="filterPlaceholder"
                    :filterFields="filterFields"
                    :filter="true"
                    :createButton="createButton"
                    :clearSearchData="!visible || clearSearchData"
                    :loading="carsDataIsLoading || loading"
                    @change="emitChange($event)"
                    @filter="searchVehicles"
                    @create-new="createVehicle"
                    :showClear="showClear">
      <template #value="slotProps">
        <div v-if="slotProps.value">
          <div class="p-d-flex p-ai-center">
            <div v-if="slotProps.value?.plate_number && !disabled" class="p-mr-1">
              <CarDataLink :showOnlyIcon="true" :plateNumber="slotProps.value.plate_number"/>
            </div>
            <div>
              {{ formatCarLabel(slotProps.value) }}
            </div>
          </div>
        </div>
        <span v-else style="visibility:hidden">.</span>
      </template>

      <template #option="slotProps">
        <div v-if="slotProps.option" class="p-d-flex p-ai-center p-jc-between">
          <div>
            {{ formatVehicleLeft(slotProps.option) }}
          </div>

          <div class="p-ml-1">
            {{ carIdentifier(slotProps.option) }}
          </div>
        </div>
      </template>
    </CustomDropdown>

    <div class="dropdown__buttons" v-show="!isDisabled">
      <Button type="button"
              v-if="localValue"
              @click="editVehicle"
              class="dropdown__edit-button p-d-flex p-ai-center p-jc-center"
              icon="ti-pencil"/>
      <Button type="button"
              @click="createVehicle"
              class="dropdown__create-button p-d-flex p-ai-center p-jc-center"
              icon="ti-plus"/>
    </div>

    <label :for="id" class="view-button-input__label">
      <span class="view-button-input__inner">
        <span class="view-button-input__text">
          {{ computedLabel }} <span v-if="countLabel">({{ countLabel }})</span>
          <span class="warning-color" v-if="required">*</span>
        </span>
        <InputViewLink :uid="localValue?.uid" :path="path"/>
      </span>
    </label>
  </div>

  <small class="p-invalid" v-if="isWrong">{{ $t('Required field') }}</small>
</template>

<script>
import httpClient from "@/services/http.services";
import InputViewLink from "@/components/InputViewLink.vue";
import CarDataLink from "@/pages/vehicles/components/CarDataLink";
import ShowErrorsMixins from "@/mixins/showErrorsMixins"
import formatMixins from "@/mixins/formatMixins";

export default {
  name: 'VehicleDropdown',
  mixins: [ShowErrorsMixins, formatMixins],
  components: { InputViewLink, CarDataLink },

  props: {
    id: { type: String, default: "order-modal-car" },
    modelValue: { type: Object, default: null },

    disabled: { type: Boolean, default: false },

    visible: { type: Boolean, default: true },

    label: { type: String, default: null },
    required: { type: Boolean, default: false },
    isWrong: { type: Boolean, default: false },

    isForkliftTenant: { type: Boolean, default: false },

    allowCreate: { type: Boolean, default: true },
    loading: { type: Boolean, default: false },

    path: { type: String, default: 'vehicles' },

    customerId: { type: [Number, String], default: null },

  },

  emits: ['update:modelValue', 'change', 'create', 'edit'],

  data() {
    return {
      cars: [],
      customerCars: [],
      carsDataIsLoading: false,
      carsApiRequestId: 0,
      carSearchData: "",
      clearSearchData: false,
    };
  },

  computed: {
    localValue: {
      get() { return this.modelValue; },
      set(v) { this.$emit("update:modelValue", v); },
    },
    computedLabel() {
      return this.label || this.$t('Vehicle')
    },
    countLabel() {
      return this.customerCars?.length
    },
    isDisabled() {
      return !!this.disabled
    },

    showClear() {
      return !!this.localValue && !this.isDisabled;
    },

    createButton() {
      return !!this.allowCreate && !this.isDisabled;
    },

    filterPlaceholder() {
      return this.isForkliftTenant
          ? this.$t('searchEquipmentPlaceholder')
          : this.$t('searchVehiclePlaceholder')
    },

    filterFields() {
      return ['plate_number', 'vin', 'serial_number', 'asset_code'];
    },

    selectClasses() {
      return {
        "dropdown__select--border-radius": this.isDisabled,
        "dropdown__select--one-button-width": !this.isDisabled && !this.localValue,
        "dropdown__select--two-buttons-width": !this.isDisabled && !!this.localValue,
        "p-invalid": this.isWrong,
      };
    },
  },

  beforeUnmount() {
    this.invalidateRequests();
  },

  watch: {
    visible: {
      flush: 'sync',
      handler(v) {
        if (!v) this.reset({ clearModel: false });
      },
    },

    customerId: {
      flush: 'sync',
      handler(value, previousValue) {
        if (this.idKey(value) === this.idKey(previousValue)) return;
        this.invalidateRequests();
        this.customerCars = [];
        this.cars = this.localValue ? [this.localValue] : [];
      },
    },
    modelValue: {
      immediate: true,
      flush: 'sync',
      handler(v, previousValue) {
        if (this.idKey(v?.id) !== this.idKey(previousValue?.id)) this.invalidateRequests();
        if (!v || typeof v !== 'object') return
        this.upsertOption(v)
      }
    },
  },

  methods: {
    invalidateRequests() {
      // Never reuse request IDs: an older response may still be in flight.
      this.carsApiRequestId++;
      this.carsDataIsLoading = false;
    },

    idKey(v) {
      return v === null || v === undefined ? "" : String(v);
    },
    upsertOption(vehicle) {
      if (!vehicle?.id) return

      const key = this.idKey(vehicle.id)
      const idx = this.cars.findIndex(v => this.idKey(v?.id) === key)

      if (idx === -1) {
        this.cars = [vehicle, ...this.cars]
      } else {
        const copy = [...this.cars]
        copy[idx] = vehicle
        this.cars = copy
      }
    },

    emitChange(event) {
      this.invalidateRequests();
      const value = event?.value ?? null;
      const hasSearch = !!(this.carSearchData || '').trim();

      if (!value) {
        if (!hasSearch) {
          this.cars = this.customerCars?.length
              ? this.composeCustomerOptions()
              : [];
        }

        this.$emit("change", null);
        return;
      }

      if (!hasSearch) {
        this.cars = this.customerCars?.length
            ? this.composeCustomerOptions(this.customerCars, value)
            : [value];
      }

      this.$emit("change", value);
    },

    formatVehicleLeft(v) {
      if (!v) return ''

      const main = [this.carMakeName(v), this.carModelName(v)].filter(Boolean).join(' ')
      const extra = v?.year || v?.vin

      return extra ? `${main ? `${main}, ` : ''}${extra}` : main
    },


    composeCustomerOptions(baseList = this.customerCars, selected = null) {
      let options = Array.isArray(baseList) ? [...baseList] : [];
      const v = selected ?? this.localValue;

      if (v?.id) {
        const selId = this.idKey(v.id);
        if (!options.some(c => this.idKey(c?.id) === selId)) {
          options.unshift(v);
        }
      }

      return options;
    },

    setOptions(list = []) {
      this.invalidateRequests();
      const baseList = Array.isArray(list) ? [...list] : [];

      // source of truth for countLabel
      this.customerCars = baseList;

      // UI options
      this.cars = this.composeCustomerOptions(baseList);

      this.carSearchData = '';
      this.clearSearchData = true;
      this.$refs.dd?.clearFilterValue?.();
    },


    async searchVehicles(event) {
      this.invalidateRequests();
      const requestId = this.carsApiRequestId;
      if (!this.visible || !this.$store.getters.userCanGetData) return;

      const value = (event?.value ?? "").toString();
      this.carSearchData = value;

      if (!value) {
        this.carsDataIsLoading = false

        if (this.customerCars?.length) {
          this.cars = this.composeCustomerOptions()
        } else {
          this.cars = this.localValue ? [this.localValue] : []
        }

        return
      }

      if (value.length <= 2) {
        this.carsDataIsLoading = false;
        this.cars = [];
        return;
      }

      this.clearSearchData = false;

      this.carsDataIsLoading = true;

      const url = "car/dropdown-vehicles?search=" + encodeURIComponent(value);

      try {
        const { status, data } = await httpClient(url);

        if (requestId !== this.carsApiRequestId) return;

        if (!this.carSearchData) {
          if (this.customerCars?.length) {
            this.cars = this.composeCustomerOptions();
          } else {
            this.cars = this.localValue ? [this.localValue] : [];
          }
          return;
        }

        if (this.carSearchData.length <= 2) {
          this.cars = [];
          return;
        }

        this.cars = (status === 200 && Array.isArray(data)) ? data : [];
      } catch (err) {
        if (requestId === this.carsApiRequestId) this.showError(err);
      } finally {
        if (requestId === this.carsApiRequestId) this.carsDataIsLoading = false;
      }
    },


    createVehicle() {
      if (!this.allowCreate || this.isDisabled) return

      let payload = {}

      if (this.carSearchData) {
        payload = { plate_number: this.carSearchData }
      }

      this.$store.commit("toggleSecondLayer", true)
      this.$emit("create", payload)
    },

    editVehicle() {
      if (!this.localValue) return;

      const payload = { ...this.localValue };

      this.$store.commit("toggleSecondLayer", true);
      this.$emit("edit", payload);
    },

    updateVehicle(vehicle, { forceSelect = false } = {}) {
      if (!vehicle) return;
      this.invalidateRequests();

      const vehicleKey = this.idKey(vehicle.id);
      const isSelected = this.idKey(this.localValue?.id) === vehicleKey;

      let nextCars = [...this.cars];
      const idx = nextCars.findIndex(v => this.idKey(v?.id) === vehicleKey);

      if (idx !== -1) {
        nextCars[idx] = {
          ...nextCars[idx],
          ...vehicle,
        };
      } else if (forceSelect || isSelected) {
        nextCars.push({ ...vehicle });
      } else {
        return;
      }

      this.cars = nextCars;

      const selected = this.cars.find(v => this.idKey(v?.id) === vehicleKey) || null;

      if (forceSelect || isSelected) {
        this.localValue = selected;
        this.emitChange({ value: selected });
      }
    },

    async getCustomerVehicles(customerId = this.customerId, { autoSelectSingle = true } = {}) {
      this.invalidateRequests();
      const requestId = this.carsApiRequestId;
      const customerKey = this.idKey(this.customerId);
      const isCurrent = () => requestId === this.carsApiRequestId
          && this.visible && customerKey === this.idKey(this.customerId);
      if (!this.visible || !this.$store.getters.userCanGetData) return [];

      const id = Number(customerId || 0);
      if (!id) {
        this.setOptions([]);
        return [];
      }

      this.carsDataIsLoading = true;

      try {
        const { status, data } = await httpClient.post(
            'car/get-customer-cars',
            { id }
        );

        if (!isCurrent()) return [];
        const list = (status === 200 && Array.isArray(data)) ? data : [];

        this.setOptions(list);

        if (autoSelectSingle && !this.localValue?.id && list.length === 1) {
          const onlyVehicle = list[0];
          this.localValue = onlyVehicle;
          this.emitChange({ value: onlyVehicle });
        }

        return list;
      } catch (err) {
        if (isCurrent()) {
          this.showError(err);
          this.setOptions([]);
        }
        return [];
      } finally {
        if (isCurrent()) this.carsDataIsLoading = false;
      }
    },

    reset({ clearModel = false, silent = false } = {}) {
      this.invalidateRequests();

      this.cars = [];
      this.customerCars = [];
      this.clearSearchData = true;

      this.$refs.dd?.clearFilterValue?.();
      this.carSearchData = "";

      if (clearModel) {
        this.localValue = null;
        this.$emit("update:modelValue", null);

        if (!silent) {
          this.$emit("change", null);
        }
      }
    }

  },
};
</script>